import { useState, useEffect, useRef } from "react";
import { Send, Trash2, MoreHorizontal } from "lucide-react";
import {
  fetchCommentaires,
  ajouterCommentaire,
  supprimerCommentaire,
  getInitiales,
  getNomAffiche,
  formatDateRelative,
  type Commentaire,
  type EntiteType,
} from "../../lib/commentaires";
import { supabase } from "../../lib/supabase";

interface CommentairesTabProps {
  dossierId: string;
  entiteType: EntiteType;
  entiteId: string;
  /** Appelé après ajout / suppression pour mettre à jour un badge compteur côté parent */
  onCommentsChanged?: () => void;
}

export default function CommentairesTab({ dossierId, entiteType, entiteId, onCommentsChanged }: CommentairesTabProps) {
  const [commentaires, setCommentaires] = useState<Commentaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [effectiveDossierId, setEffectiveDossierId] = useState<string>(dossierId);
  /** IDs de commentaires dont l’image avatar a échoué au chargement → initiales */
  const [avatarLoadFailed, setAvatarLoadFailed] = useState<Set<string>>(() => new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const font = "'IBM Plex Sans', system-ui, sans-serif";
  const extractErrorMessage = (e: unknown): string => {
    if (e instanceof Error && e.message?.trim()) return e.message.trim();
    if (typeof e === "object" && e !== null) {
      const err = e as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown };
      const message = typeof err.message === "string" ? err.message.trim() : "";
      const details = typeof err.details === "string" ? err.details.trim() : "";
      const hint = typeof err.hint === "string" ? err.hint.trim() : "";
      const code = typeof err.code === "string" ? err.code.trim() : "";
      return [message, details, hint, code ? `Code: ${code}` : ""].filter(Boolean).join(" — ");
    }
    return "Impossible d'envoyer le commentaire.";
  };

  // Charger l'utilisateur courant
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id || null));
  }, []);

  // Résout le dossier réel de l'entité pour éviter un contexte UI divergent.
  useEffect(() => {
    let cancelled = false;
    setEffectiveDossierId(dossierId);
    if (entiteType !== "transaction" || !entiteId) return;
    void (async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select("dossier_id")
        .eq("id", entiteId)
        .maybeSingle();
      if (cancelled || error) return;
      const txDossierId = String((data as { dossier_id?: string | null } | null)?.dossier_id ?? "").trim();
      if (txDossierId && txDossierId !== dossierId) {
        console.warn("[CommentairesTab] dossier mismatch, use transaction dossier", {
          ui_dossier_id: dossierId,
          transaction_dossier_id: txDossierId,
          entiteType,
          entiteId,
        });
      }
      if (txDossierId) setEffectiveDossierId(txDossierId);
    })();
    return () => {
      cancelled = true;
    };
  }, [dossierId, entiteType, entiteId]);

  // Charger les commentaires
  const loadCommentaires = async () => {
    try {
      const data = await fetchCommentaires(entiteType, entiteId, effectiveDossierId);
      setCommentaires(data);
    } catch (e) {
      console.error("Erreur chargement commentaires:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCommentaires();
  }, [entiteType, entiteId, effectiveDossierId]);

  // Auto-scroll vers le bas
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [commentaires]);

  // Envoyer un commentaire
  const handleSend = async () => {
    if (!message.trim() || sending) return;

    setSending(true);
    setSendError(null);
    try {
      console.log("[CommentairesTab] send payload", {
        dossierId: effectiveDossierId,
        entiteType,
        entiteId,
        message: message.trim(),
      });
      await ajouterCommentaire(effectiveDossierId, entiteType, entiteId, message);
      setMessage("");
      await loadCommentaires();
      onCommentsChanged?.();
    } catch (e) {
      console.error("Erreur envoi commentaire:", e);
      setSendError(extractErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  // Supprimer un commentaire
  const handleDelete = async (id: string) => {
    try {
      await supprimerCommentaire(id);
      setCommentaires((prev) => prev.filter((c) => c.id !== id));
      setMenuOpen(null);
      onCommentsChanged?.();
    } catch (e) {
      console.error("Erreur suppression:", e);
    }
  };

  // Couleur avatar selon rôle
  const getAvatarColor = (commentaire: Commentaire) => {
    if (commentaire.user_id === currentUserId) return { bg: "#fff7ed", color: "#ea580c" };
    return { bg: "#f1f5f9", color: "#475569" };
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontFamily: font }}>
        Chargement...
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 300, fontFamily: font }}>
      {/* Liste des commentaires */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 20px 16px 22px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {commentaires.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#94a3b8",
              padding: "40px 20px",
              fontSize: 14,
            }}
          >
            Aucun commentaire pour le moment.
            <br />
            <span style={{ fontSize: 13, color: "#cbd5e1" }}>
              Soyez le premier à commenter !
            </span>
          </div>
        ) : (
          commentaires.map((c) => {
            const isOwn = c.user_id === currentUserId;
            const avatarColors = getAvatarColor(c);
            const photoUrl = c.avatar_url?.trim();
            const showAvatarImg = Boolean(photoUrl) && !avatarLoadFailed.has(c.id);

            return (
              <div
                key={c.id}
                style={{
                  display: "flex",
                  gap: 10,
                  flexDirection: isOwn ? "row-reverse" : "row",
                }}
              >
                {/* Avatar */}
                {showAvatarImg ? (
                  <img
                    src={photoUrl}
                    alt=""
                    width={36}
                    height={36}
                    onError={() =>
                      setAvatarLoadFailed((prev) => new Set(prev).add(c.id))
                    }
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: avatarColors.bg,
                      color: avatarColors.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {getInitiales(c)}
                  </div>
                )}

                {/* Bulle */}
                <div
                  style={{
                    maxWidth: "75%",
                    background: isOwn ? "#fff7ed" : "#e2e8f0",
                    border: isOwn ? "1px solid #fed7aa" : "1px solid #cbd5e1",
                    borderRadius: isOwn ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    padding: "10px 14px",
                    position: "relative",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#334155", marginRight: 4 }}>
                      {isOwn ? "Vous" : getNomAffiche(c)}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>
                        {formatDateRelative(c.created_at)}
                      </span>
                      {isOwn && (
                        <div style={{ position: "relative" }}>
                          <button
                            onClick={() => setMenuOpen(menuOpen === c.id ? null : c.id)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 2,
                              color: "#94a3b8",
                            }}
                          >
                            <MoreHorizontal size={14} />
                          </button>
                          {menuOpen === c.id && (
                            <>
                              <div
                                style={{ position: "fixed", inset: 0, zIndex: 10 }}
                                onClick={() => setMenuOpen(null)}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  right: 0,
                                  background: "white",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: 8,
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  zIndex: 11,
                                  overflow: "hidden",
                                }}
                              >
                                <button
                                  onClick={() => handleDelete(c.id)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "8px 12px",
                                    border: "none",
                                    background: "none",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    color: "#ef4444",
                                    width: "100%",
                                  }}
                                >
                                  <Trash2 size={14} />
                                  Supprimer
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <p style={{ margin: 0, fontSize: 14, color: "#1e293b", lineHeight: 1.5 }}>
                    {c.message}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input */}
      <div
        style={{
          padding: "12px 16px",
          borderTop: "1px solid #e2e8f0",
          background: "#fafafa",
          display: "flex",
          gap: 10,
        }}
      >
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSend();
            }
          }}
          placeholder="Écrire un commentaire..."
          style={{
            flex: 1,
            padding: "10px 14px",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            fontSize: 14,
            outline: "none",
            fontFamily: font,
          }}
        />
        <button
          onClick={handleSend}
          disabled={!message.trim() || sending}
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            border: "none",
            background: message.trim() ? "linear-gradient(135deg, #f97316, #eab308)" : "#e2e8f0",
            color: message.trim() ? "white" : "#94a3b8",
            cursor: message.trim() ? "pointer" : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Send size={18} />
        </button>
      </div>
      {sendError ? (
        <div
          style={{
            padding: "8px 16px 12px",
            fontSize: 12,
            color: "#b91c1c",
            background: "#fff1f2",
            borderTop: "1px solid #fecdd3",
          }}
        >
          {sendError}
        </div>
      ) : null}
    </div>
  );
}
